import React from 'react'
import styled from 'styled-components'

import { Result } from '@blade/std-lib'

import { loadPinterestWidget } from './load-pinterest-widget'

import type { Lang } from '@root/shared/localization/model/Lang'

export interface PinterestPinEmbedProps {
  embedUrl: string
  resourceType: 'pin' | 'user' | 'board'
  lang: Lang
  size?: 'small' | 'medium' | 'large'
  columns?: number
  onLoad?: () => void
  onError?: (error: unknown) => void
}

const pinDoMap: Record<PinterestPinEmbedProps['resourceType'], string> = {
  pin: 'embedPin',
  user: 'embedUser',
  board: 'embedBoard',
}

export function PinterestEmbed({
  embedUrl,
  resourceType,
  lang,
  size,
  columns,
  onLoad,
  onError,
}: PinterestPinEmbedProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const onLoadRef = React.useRef(onLoad)
  const onErrorRef = React.useRef(onError)

  React.useLayoutEffect(() => {
    onLoadRef.current = onLoad
    onErrorRef.current = onError
  })

  React.useEffect(() => {
    const container = ref.current

    if (!container) {
      return
    }

    let cancelled = false

    container.replaceChildren()

    const anchor = document.createElement('a')
    anchor.dataset.pinDo = pinDoMap[resourceType]
    anchor.dataset.pinLang = lang
    if (columns) {
      anchor.dataset.pinColumns = columns.toString()
    }
    if (size) {
      anchor.dataset.pinWidth = size
    }

    anchor.href = embedUrl
    container.appendChild(anchor)

    void (async () => {
      const result = await loadPinterestWidget()

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (cancelled) {
        return
      }

      if (Result.isFailure(result)) {
        onErrorRef.current?.(result.failure)
        return
      }

      try {
        result.success.build(container)
        onLoadRef.current?.()
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!cancelled) {
          onErrorRef.current?.(error)
        }
      }
    })()

    return () => {
      cancelled = true
      container.replaceChildren()
    }
  }, [embedUrl, resourceType, lang, size, columns])

  return <Root ref={ref} />
}

const Root = styled.div`
  width: 100%;
  /* display: grid; */
  & > * {
    display: block !important;
    width: 100% !important;
  }
`
